/**
 * The ONE precedence every surface uses to read a property's current highest bid.
 *
 * Two shapes carry it and they arrive from different places: `highestBidAmount` is the live scalar a FeedDO delta
 * writes, and `highestBid.amount` comes from a full property payload. A card that reads only the nested one shows no
 * price for a property whose bid arrived over the feed -- which is exactly what happened: the mobile feed card was
 * updated to prefer the scalar and the DESKTOP grid card was not, so the same bid rendered on one layout and not the
 * other, with the event correct on the wire both times.
 *
 * Exists so that stays impossible: adding a third reader means calling this, not re-deriving the precedence.
 */

import { numberOrZero } from '@federa/const/number-guards';
import type { NestedHighestBid } from '@federa/types/properties-auction';

/** Only the two bid-bearing fields are read; callers pass whole properties of several shapes. */
interface HighestBidSource {
	highestBidAmount?: number | null;
	highestBid?: NestedHighestBid;
}

/**
 * @returns the amount, or 0 when the property has no bid -- the value the price-display helper treats as "no bid yet".
 *
 * A numeric ZERO scalar wins over a nested amount rather than falling through, because zero is a real answer from the
 * feed; only null and undefined mean "this frame carried no bid".
 */
export function resolveHighestBidAmount(property: HighestBidSource | null | undefined): number {
	if (typeof property?.highestBidAmount === 'number') return property.highestBidAmount;
	const nested = property?.highestBid?.amount;
	return numberOrZero(nested);
}
